import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  buildFocusTaxCodeSearchPayload,
  FocusTaxCodeMode,
  FocusTaxCodeOption,
  FocusTaxCodeType,
} from "@/lib/fiscal/focus-tax-codes";

interface FocusTaxCodeSearchResponse {
  options: FocusTaxCodeOption[];
  totalCount?: number;
  offset?: number;
}

interface UseFocusTaxCodeSearchInput {
  type: FocusTaxCodeType;
  query: string;
  mode?: FocusTaxCodeMode;
  offset?: number;
  enabled?: boolean;
}

const DEBOUNCE_MS = 300;

export function useDebouncedValue<T>(value: T, delayMs = DEBOUNCE_MS) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => setDebouncedValue(value), delayMs);
    return () => window.clearTimeout(timeoutId);
  }, [delayMs, value]);

  return debouncedValue;
}

export function useFocusTaxCodeSearch({
  type,
  query,
  mode,
  offset = 0,
  enabled = true,
}: UseFocusTaxCodeSearchInput) {
  const debouncedQuery = useDebouncedValue(query);
  const payload = useMemo(
    () => buildFocusTaxCodeSearchPayload({ type, query: debouncedQuery, mode, offset }),
    [debouncedQuery, mode, offset, type]
  );

  const hasQuery = payload.query.trim().length > 0;
  const shouldQuery = enabled && (mode === "exact" ? hasQuery : type === "cfop" || hasQuery);

  return useQuery({
    queryKey: ["focus-tax-codes", payload.type, payload.mode, payload.query, payload.offset],
    enabled: shouldQuery,
    staleTime: 1000 * 60 * 10,
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke<FocusTaxCodeSearchResponse>(
        "focus-tax-codes-search",
        { body: payload }
      );

      if (error) throw error;
      return data ?? { options: [], totalCount: 0, offset };
    },
  });
}
